import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgePercent } from "lucide-react";
import { SectionHead } from "@/components/site/PageHeader";
import { Stagger, Item } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

export const useCountdown = (until) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!until) return null;
  const diff = new Date(`${until}T23:59:59`).getTime() - now;
  if (Number.isNaN(diff)) return null;
  if (diff <= 0) return { expired: true, d: 0, h: 0 };
  return { expired: false, d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000) };
};

const prettyDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export const OfferCard = ({ offer, vehicle, wide = false, showTerms = false }) => {
  const cd = useCountdown(offer.valid_until);
  const image = offer.image || vehicle?.hero_image;
  return (
    <article
      data-testid={`offer-card-${offer.id}`}
      className={cn("group relative flex flex-col overflow-hidden border border-gold/25 bg-surface transition-[border-color] duration-500 hover:border-gold/60", wide && "lg:grid lg:grid-cols-12")}
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl transition-opacity duration-700 group-hover:opacity-100" />
      <span className="absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-gold transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />

      <div className={cn("relative flex flex-1 flex-col p-7 sm:p-10", wide && "lg:col-span-7")}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-crimson px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white">
            <BadgePercent className="h-3 w-3" /> {offer.badge || "Offer"}
          </span>
          {cd && !cd.expired && (
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid={`offer-countdown-${offer.id}`}>
              Ends in {cd.d}d {cd.h}h
            </span>
          )}
        </div>
        {offer.subtitle && <p className="eyebrow mt-8">{offer.subtitle}</p>}
        <h3 className={cn("mt-3 font-display font-bold uppercase tracking-[-0.02em] text-white", wide ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl")}>{offer.title}</h3>
        {offer.discount_label && <p className={cn("mt-3 font-editorial italic text-gold", wide ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl")}>{offer.discount_label}</p>}
        {offer.description && <p className="mt-6 max-w-xl leading-relaxed text-slate-400">{offer.description}</p>}
        {showTerms && offer.terms && <p className="mt-5 max-w-xl text-xs leading-relaxed text-slate-500">{offer.terms}</p>}

        <div className="mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 sm:pt-8" style={{ marginTop: "2rem" }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
            {offer.valid_until ? `Valid until ${prettyDate(offer.valid_until)}` : "While stock lasts"}
            {vehicle && <> · <Link to={`/models/${vehicle.slug}`} className="text-slate-300 hover:text-white">{vehicle.name}</Link></>}
          </p>
          <Link to={offer.cta_link || "/contact"} data-testid={`offer-cta-${offer.id}`} className="btn-crimson !px-5 !py-2.5">
            {offer.cta_label || "Enquire now"} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {image && (
        <div className={cn("relative flex items-center justify-center overflow-hidden border-t border-white/10 px-8 py-8", wide ? "lg:col-span-5 lg:border-l lg:border-t-0" : "")}>
          <div className="spotlight absolute inset-0 opacity-70" />
          <img src={image} alt={offer.title} loading="lazy" className={cn("relative z-10 w-full object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.6)] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-3 group-hover:scale-[1.04]", wide ? "max-h-[300px]" : "max-h-[200px]")} />
        </div>
      )}
    </article>
  );
};

export const OffersSection = ({ offers = [], vehicles = [], number = "02" }) => {
  if (!offers.length) return null;
  const shown = offers.slice(0, 2);
  return (
    <section id="offers" data-testid="offers-section" className="relative border-t border-white/10 py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.07),transparent_55%)]" />
      <div className="wrap relative">
        <SectionHead
          number={number}
          eyebrow="Offers"
          lines={["Limited-time", "deals."]}
          sub="Anniversary pricing, launch bundles and showroom-only deals. When they're gone, they're gone."
          right={
            offers.length > 2 && (
              <Link to="/offers" data-testid="offers-all-link" className="btn-ghost">
                All offers <ArrowUpRight className="h-4 w-4" />
              </Link>
            )
          }
        />
        <Stagger className={cn("grid gap-4", shown.length > 1 && "md:grid-cols-2")} gap={0.12}>
          {shown.map((o) => (
            <Item key={o.id} className="h-full">
              <OfferCard offer={o} vehicle={vehicles.find((v) => v.slug === o.vehicle_slug)} wide={shown.length === 1} />
            </Item>
          ))}
        </Stagger>
      </div>
    </section>
  );
};
